/**
 * ResourceTagInput — a contenteditable rich-text input that supports inline resource chips.
 *
 * Type `@` to open a resource-search popup; the text the user keeps typing
 * after the `@` (in the editor itself, not a separate input) becomes the
 * search query. Selecting a result inserts a styled, non-editable chip at the
 * cursor position, inline with the surrounding text, replacing the `@query`
 * substring. Chips are removable via their × button.  On submission the parent calls
 * `getContent()` which walks the DOM and returns an ordered array of ``EditorContentPart``
 * objects — either ``TextInputPart`` or ``ResourceInputPart`` — interleaved as they
 * appear in the editor.
 *
 * Usage:
 *   const ref = useRef<ResourceTagInputHandle>(null);
 *   <ResourceTagInput ref={ref} placeholder="Ask..." onEnter={submit} onHasContentChange={setHas} onPastedImages={addFiles} />
 *
 * Pass ``suggestion`` to show a ghost prefill in place of the placeholder; Tab
 * on an empty editor accepts it into the editor.
 *   // on submit:
 *   const { parts, hasText } = ref.current.getContent();
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'src/components/ui/toast';
import { FileTextIcon } from '@phosphor-icons/react';
import { apiGet } from 'src/lib/api/axios';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import Popover from 'src/components/ui/Popover';
import { twMerge } from 'tailwind-merge';
import { LinkChip } from './LinkChip';
import { RESOURCE_ICON, resourcePath } from './ResourceLink';
import { canvasDocumentPath } from '../urls';

// ─── Public types ─────────────────────────────────────────────────────────────

export type ResourceSearchType =
  | 'chat'
  | 'review'
  | 'writing'
  | 'document'
  | 'action'
  | 'action_extraction'
  | 'group'
  | 'team_group'
  | 'dashboard'
  | 'specialist'
  | 'meeting'
  | 'meeting_series'
  | 'asset_review'
  | 'extraction';

export interface ResourceSearchItem {
  resource_type: ResourceSearchType;
  external_id: string;
  title: string;
  updated_at?: string | null;
  /** Set when the hit is a canvas. The click opens that canvas, not the backing file. */
  session_id?: string;
  canvas_id?: string;
}

function searchItemPath(item: ResourceSearchItem): string {
  if ((item.resource_type as string) === 'canvas' && item.session_id && item.canvas_id) {
    return canvasDocumentPath({ session_id: item.session_id, canvas_id: item.canvas_id });
  }
  return resourcePath(item.resource_type, item.external_id);
}

export type TextInputPart = { type: 'text'; text: string };
export type ResourceInputPart = {
  type: 'resource';
  resource_id: string;
  resource_type?: string;
  title?: string;
};
/** A part returned by getContent() — either a text segment or an inline resource chip. */
export type EditorContentPart = TextInputPart | ResourceInputPart;

/** Imperative handle for the parent to control the editor programmatically. */
export interface ResourceTagInputHandle {
  focus(): void;
  /** Focus the editor and place the caret after the existing text. */
  focusAtEnd(): void;
  /** Remove all content (text + chips). */
  clear(): void;
  /** Replace the entire editor content with plain text (chips are lost). */
  setPlainText(text: string): void;
  /** Append plain text at the end (used for voice transcription). */
  appendText(text: string): void;
  /** Restore the editor to a previously saved innerHTML (chips are preserved). */
  setHTML(html: string): void;
  /** Snapshot the current innerHTML for later restore. */
  getHTML(): string;
  /**
   * Walk the DOM and return ordered content parts (text segments and resource
   * chips interleaved as they appear). Empty text segments are omitted.
   * Also returns a convenience ``hasText`` flag indicating whether any
   * non-whitespace text is present.
   */
  getContent(): { parts: EditorContentPart[]; hasText: boolean };
  /** Open the resource-search popup programmatically (e.g. from a toolbar button). */
  openResourceSearch(): void;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Character that opens the resource-search popup when typed in the editor. */
const TRIGGER_CHAR = '@';

/** Route segment → resource type mappings for URL paste detection. */
const URL_ROUTE_PATTERNS: Array<{ pattern: RegExp; type: ResourceSearchType }> = [
  { pattern: /\/reporting\/dashboard\/([^/?#]+)/, type: 'dashboard' },
  { pattern: /\/modules\/pfi\/asset-review\/([^/?#]+)/, type: 'asset_review' },
  { pattern: /\/meeting_series\/([^/?#]+)/, type: 'meeting_series' },
  { pattern: /\/meetings\/([^/?#]+)/, type: 'meeting' },
  { pattern: /\/groups\/([^/?#]+)/, type: 'team_group' },
  { pattern: /\/review\/([^/?#]+)/, type: 'review' },
  { pattern: /\/write\/([^/?#]+)/, type: 'writing' },
  { pattern: /\/wiki\/([^/?#]+)/, type: 'document' },
  { pattern: /\/chat\/([^/?#]+)/, type: 'chat' }
];

interface ParsedResourceUrl {
  resourceId: string;
  resourceType: ResourceSearchType;
}

/**
 * Parse a pasted URL into a resource type + ID, or return null if it isn't a
 * recognised app URL on the current host.  The origin check prevents URLs
 * from unrelated sites (e.g. bbc.com/tenant/chat/...) from being treated as
 * resource links.
 */
function parseResourceUrl(text: string): ParsedResourceUrl | null {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) {
    return null;
  }
  const { pathname } = url;
  for (const { pattern, type } of URL_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (match?.[1]) {
      return { resourceId: match[1], resourceType: type };
    }
  }
  return null;
}

const RESOURCE_SEARCH_LABEL: Record<ResourceSearchType, string> = {
  chat: 'Chat',
  review: 'Review',
  writing: 'Writing',
  document: 'Document',
  action: 'Action',
  action_extraction: 'Action Extraction',
  group: 'Group',
  team_group: 'Group',
  dashboard: 'Dashboard',
  specialist: 'Specialist',
  meeting: 'Meeting',
  meeting_series: 'Meeting Series',
  asset_review: 'Asset Review',
  extraction: 'Action Extraction'
};

function ResourceSearchIcon({ resourceType, size = 16 }: { resourceType: ResourceSearchType; size?: number }) {
  const Icon = RESOURCE_ICON[resourceType] ?? FileTextIcon;
  return <Icon size={size} weight="light" className="text-content-subtle shrink-0" />;
}

/** Resource types shown as filter pills in the search popup. */
const FILTER_OPTIONS: Array<{ type: ResourceSearchType; label: string }> = [
  { type: 'action', label: 'Actions' },
  { type: 'document', label: 'Documents' },
  { type: 'chat', label: 'Chats' },
  { type: 'meeting', label: 'Meetings' },
  { type: 'team_group', label: 'Groups' },
  { type: 'writing', label: 'Writings' }
];

// ─── Component ────────────────────────────────────────────────────────────────

interface ResourceTagInputProps {
  placeholder?: string;
  /** Ghost text shown instead of the placeholder while empty; Tab accepts it. */
  suggestion?: string;
  /** Called after the user accepts ``suggestion`` with Tab. */
  onSuggestionAccepted?: (suggestion: string) => void;
  /** Called when Enter (without Shift) is pressed. */
  onEnter?: () => void;
  /** Called whenever the "has content" state changes (text or chips present). */
  onHasContentChange?: (hasContent: boolean) => void;
  /**
   * Called when the user pastes image files.  The parent is responsible for
   * uploading them; pass `{ filterTypes: false }` to skip MIME validation.
   */
  onPastedImages?: (files: File[]) => void;
  className?: string;
  /** Extra classes for the editor, e.g. a taller min height on the homepage. */
  editorClassName?: string;
  autoFocus?: boolean;
  /** When true, the editor cannot be focused or edited. */
  disabled?: boolean;
}

const ResourceTagInput = forwardRef<ResourceTagInputHandle, ResourceTagInputProps>(
  (
    {
      placeholder,
      suggestion,
      onSuggestionAccepted,
      onEnter,
      onHasContentChange,
      onPastedImages,
      className,
      editorClassName,
      autoFocus = true,
      disabled = false
    },
    ref
  ) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // The trigger-character Text node that anchors the current search
    // session. The query is the text in the editor between (just after) this
    // node and the cursor.
    const triggerAnchorRef = useRef<Text | null>(null);

    const [isEmpty, setIsEmpty] = useState(true);
    // Each entry pairs a DOM container (inside contenteditable) with the
    // ResourceSearchItem that should be rendered into it via a portal.
    const [chipPortals, setChipPortals] = useState<Array<{ container: HTMLSpanElement; item: ResourceSearchItem }>>([]);
    // Prevents the MutationObserver from interfering during programmatic innerHTML changes.
    const isRestoringRef = useRef(false);
    const { pathname } = useLocation();
    const tenantCode = resolveTenantCode(pathname);

    const [resourceSearchOpen, setResourceSearchOpen] = useState(false);
    const [resourceSearchQuery, setResourceSearchQuery] = useState('');
    const [debouncedResourceQuery, setDebouncedResourceQuery] = useState('');
    const [resourceTypeFilter, setResourceTypeFilter] = useState<ResourceSearchType | null>(null);
    // Keyboard-navigation index within the current search results
    const [selectedIndex, setSelectedIndex] = useState(0);
    const resultsListRef = useRef<HTMLUListElement>(null);

    // ── Debounce search query ────────────────────────────────────────────────
    useEffect(() => {
      const id = setTimeout(() => setDebouncedResourceQuery(resourceSearchQuery.trim()), 250);
      return () => clearTimeout(id);
    }, [resourceSearchQuery]);

    // ── Resource search API ──────────────────────────────────────────────────
    const {
      data: resourceSearchData,
      isFetching: resourceSearchFetching,
      isPending: resourceSearchPending
    } = useQuery({
      queryKey: ['chat-resource-tag-search', debouncedResourceQuery, resourceTypeFilter],
      queryFn: async () => {
        const params: Record<string, unknown> = { query: debouncedResourceQuery, k: 8 };
        if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
        const response = await apiGet<{ items: ResourceSearchItem[] }>('/search/', {
          params,
          meta: { globalError: false, background: true, context: 'chat-resource-tag-search' }
        });
        return response.data;
      },
      enabled: debouncedResourceQuery.length > 0,
      staleTime: 30_000
    });

    const resourceSearchResults = useMemo(() => resourceSearchData?.items ?? [], [resourceSearchData]);
    const isResourceSearching =
      (resourceSearchQuery.trim().length > 0 && resourceSearchQuery.trim() !== debouncedResourceQuery) ||
      (debouncedResourceQuery.length > 0 && (resourceSearchFetching || resourceSearchPending));

    // Auto-select first result whenever the results list changes
    useEffect(() => {
      setSelectedIndex(0);
    }, [resourceSearchResults]);

    // Scroll the highlighted item into view when index changes
    useEffect(() => {
      const list = resultsListRef.current;
      if (!list) return;
      const item = list.children[selectedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    // ── Internal helpers ─────────────────────────────────────────────────────

    const notifyHasContent = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      const hasContent = !!(el.textContent?.trim() || el.querySelector('[data-resource-id]'));
      setIsEmpty(!hasContent);
      onHasContentChange?.(hasContent);
    }, [onHasContentChange]);

    const adjustHeight = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 128) + 'px';
    }, []);

    const moveCursorToEnd = useCallback((el: HTMLElement) => {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }, []);

    /**
     * Create an empty DOM container for a chip. React fills it with a
     * ``LinkChip`` via a portal registered in ``chipPortals``.
     */
    const buildChip = useCallback((item: ResourceSearchItem): HTMLSpanElement => {
      const container = document.createElement('span');
      container.setAttribute('contenteditable', 'false');
      container.dataset.resourceId = item.external_id;
      container.dataset.resourceType = item.resource_type;
      container.dataset.resourceTitle = item.title;
      if (item.session_id) container.dataset.sessionId = item.session_id;
      if (item.canvas_id) container.dataset.canvasId = item.canvas_id;
      // Keep as inline so the portalled LinkChip controls its own baseline
      // alignment — inline-flex or inline-block here would fight the chip's
      // own text-flow positioning and cause vertical misalignment.
      container.style.display = 'inline';
      setChipPortals((prev) => [...prev, { container, item }]);
      return container;
    }, []);

    /** Remove a chip by resource ID — updates both DOM and portal state. */
    const removeChip = useCallback(
      (externalId: string) => {
        const editor = editorRef.current;
        const chip = editor?.querySelector(`[data-resource-id="${externalId}"]`) as HTMLElement | null;
        if (chip) {
          const range = document.createRange();
          const sel = window.getSelection();
          range.setStartBefore(chip);
          range.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(range);
          chip.remove();
        }
        setChipPortals((prev) => prev.filter((p) => p.item.external_id !== externalId));
        notifyHasContent();
        adjustHeight();
      },
      [notifyHasContent, adjustHeight]
    );

    /** Walk the editor DOM and return ordered content parts (text + resource chips). */
    const extractContent = useCallback((): { parts: EditorContentPart[]; hasText: boolean } => {
      const editor = editorRef.current;
      if (!editor) return { parts: [], hasText: false };

      const parts: EditorContentPart[] = [];
      let textBuffer = '';

      const flushText = () => {
        if (textBuffer) {
          parts.push({ type: 'text', text: textBuffer });
          textBuffer = '';
        }
      };

      const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          textBuffer += node.textContent ?? '';
        } else if (node instanceof HTMLElement) {
          const resourceId = node.dataset.resourceId;
          if (resourceId) {
            // Flush any accumulated text before the chip
            flushText();
            parts.push({
              type: 'resource',
              resource_id: resourceId,
              resource_type: node.dataset.resourceType,
              title: node.dataset.resourceTitle
            });
          } else if (node.tagName === 'BR') {
            textBuffer += '\n';
          } else if (node.tagName === 'DIV' && node !== editor) {
            textBuffer += '\n';
            node.childNodes.forEach(walk);
          } else {
            node.childNodes.forEach(walk);
          }
        }
      };

      editor.childNodes.forEach(walk);

      // Flush any remaining text, trimming only leading/trailing whitespace of the full message
      const trimmedBuffer = textBuffer.trimEnd();
      if (trimmedBuffer) {
        parts.push({ type: 'text', text: trimmedBuffer });
      }

      // Trim leading whitespace from the first text part
      if (parts.length > 0 && parts[0].type === 'text') {
        const trimmed = parts[0].text.trimStart();
        if (trimmed) {
          parts[0] = { type: 'text', text: trimmed };
        } else {
          parts.shift();
        }
      }

      const hasText = parts.some((p) => p.type === 'text' && p.text.trim().length > 0);
      return { parts, hasText };
    }, []);

    // ── Popup open/close ─────────────────────────────────────────────────────

    const closeSearch = useCallback(() => {
      setResourceSearchOpen(false);
      setResourceSearchQuery('');
      setDebouncedResourceQuery('');
      setResourceTypeFilter(null);
      triggerAnchorRef.current = null;
    }, []);

    /**
     * Read the text in the editor between the trigger anchor and the current
     * cursor and push it into ``resourceSearchQuery``. Closes the popup if
     * the trigger character has been deleted or the cursor has moved before it.
     */
    const updateQueryFromEditor = useCallback(() => {
      const editor = editorRef.current;
      const triggerNode = triggerAnchorRef.current;
      if (!editor || !triggerNode) return;

      const triggerText = triggerNode.textContent ?? '';
      if (!editor.contains(triggerNode) || triggerText[0] !== TRIGGER_CHAR) {
        closeSearch();
        return;
      }

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      if (!sel.anchorNode || !editor.contains(sel.anchorNode)) return;

      const cursor = sel.getRangeAt(0);
      if (!cursor.collapsed) return;

      // The position immediately after the trigger character.
      const afterTrigger = document.createRange();
      try {
        afterTrigger.setStart(triggerNode, 1);
      } catch {
        closeSearch();
        return;
      }
      afterTrigger.collapse(true);

      // Cursor moved to before the trigger → bail out.
      if (afterTrigger.compareBoundaryPoints(Range.START_TO_START, cursor) > 0) {
        closeSearch();
        return;
      }

      const queryRange = document.createRange();
      try {
        queryRange.setStart(triggerNode, 1);
        queryRange.setEnd(cursor.endContainer, cursor.endOffset);
      } catch {
        closeSearch();
        return;
      }
      const query = queryRange.toString();
      // Close the search if:
      // 1. Query starts with whitespace (user backspaced past @ or moved cursor before @)
      // 2. Query ends with double space or newline/tab (clear signal user has moved on)
      // Allow single spaces within the query to support multi-word resource names.
      if (/^\s/.test(query) || /\s{2,}$/.test(query) || /[\n\t]/.test(query)) {
        closeSearch();
        return;
      }
      setResourceSearchQuery(query);
    }, [closeSearch]);

    /**
     * Whether the cursor sits at a "word boundary" — i.e. the character to its
     * left is missing, whitespace, or non-word. Used to suppress the resource
     * picker when '@' is typed inside an existing word (e.g. an email).
     */
    const isAtWordBoundary = useCallback((): boolean => {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (!editor || !sel || sel.rangeCount === 0) return true;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return true;

      const beforeRange = document.createRange();
      try {
        beforeRange.setStart(editor, 0);
        beforeRange.setEnd(range.endContainer, range.endOffset);
      } catch {
        return true;
      }
      const text = beforeRange.toString();
      if (text.length === 0) return true;
      return !/\w/.test(text[text.length - 1]);
    }, []);

    /**
     * Called when the user pastes a recognised app URL.  Fetches the resource
     * preview and inserts a chip at the saved cursor position.  Falls back to
     * inserting the raw URL text if the lookup fails (resource not found /
     * permission denied).
     */
    const handleResourceUrlPaste = useCallback(
      async (parsed: ParsedResourceUrl, originalUrl: string, savedRange: Range | null) => {
        const editor = editorRef.current;
        if (!editor) return;

        const restoreRange = () => {
          if (!savedRange) return;
          const sel = window.getSelection();
          sel?.removeAllRanges();
          try {
            sel?.addRange(savedRange);
          } catch {
            // Range may have become stale; fall through to end-of-editor
          }
        };

        try {
          const response = await apiGet<{ id: string; resource_type: string; title: string }>(
            `/resources/${parsed.resourceId}/preview`,
            { meta: { globalError: false, background: true, context: 'resource-url-paste' } }
          );
          const { id, resource_type, title } = response.data;
          const item: ResourceSearchItem = {
            external_id: id,
            resource_type: resource_type as ResourceSearchType,
            title
          };

          restoreRange();
          editor.focus();

          const chip = buildChip(item);
          const sel = window.getSelection();
          let range: Range;
          if (sel && sel.rangeCount > 0) {
            range = sel.getRangeAt(0);
            range.deleteContents();
          } else {
            range = document.createRange();
            range.selectNodeContents(editor);
            range.collapse(false);
          }
          range.insertNode(chip);
          const space = document.createTextNode('\u00a0');
          range.setStartAfter(chip);
          range.insertNode(space);
          const cursorRange = document.createRange();
          cursorRange.setStartAfter(space);
          cursorRange.collapse(true);
          notifyHasContent();
          adjustHeight();
          requestAnimationFrame(() => {
            const s = window.getSelection();
            s?.removeAllRanges();
            s?.addRange(cursorRange);
            editor.focus();
          });
        } catch {
          // Resource not accessible — insert the URL as plain text
          restoreRange();
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(originalUrl));
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          } else {
            editor.appendChild(document.createTextNode(originalUrl));
          }
          notifyHasContent();
          adjustHeight();
        }
      },
      [buildChip, notifyHasContent, adjustHeight]
    );

    /**
     * Insert the trigger character at the cursor and open the resource-search
     * popup. Focus is kept in the editor — subsequent typing populates the
     * search query.
     */
    const openSearch = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return;

      editor.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const range = sel.getRangeAt(0);
      range.deleteContents();
      const triggerNode = document.createTextNode(TRIGGER_CHAR);
      range.insertNode(triggerNode);
      range.setStartAfter(triggerNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      triggerAnchorRef.current = triggerNode;

      setResourceSearchOpen(true);
      setResourceSearchQuery('');
      setDebouncedResourceQuery('');
      notifyHasContent();
    }, [notifyHasContent]);

    const insertChip = useCallback(
      (item: ResourceSearchItem) => {
        const editor = editorRef.current;
        if (!editor) {
          closeSearch();
          return;
        }

        editor.focus();
        const chip = buildChip(item);
        const sel = window.getSelection();

        // Replace the trigger character (and any query text typed after it)
        // with the chip. Fall back to inserting at end-of-editor if the
        // anchor is gone.
        const triggerNode = triggerAnchorRef.current;
        let range: Range;
        if (triggerNode && editor.contains(triggerNode)) {
          range = document.createRange();
          range.setStartBefore(triggerNode);
          if (sel && sel.rangeCount > 0) {
            const cursor = sel.getRangeAt(0);
            try {
              range.setEnd(cursor.endContainer, cursor.endOffset);
            } catch {
              range.setEndAfter(triggerNode);
            }
          } else {
            range.setEndAfter(triggerNode);
          }
        } else {
          range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
        }

        triggerAnchorRef.current = null;
        range.deleteContents();
        range.insertNode(chip);

        // Insert a space after the chip so the user can keep typing immediately
        const space = document.createTextNode('\u00a0'); // non-breaking space stays visible
        range.setStartAfter(chip);
        range.insertNode(space);

        // Snapshot where we want the cursor — after the space
        const cursorRange = document.createRange();
        cursorRange.setStartAfter(space);
        cursorRange.collapse(true);

        notifyHasContent();
        adjustHeight();
        closeSearch();

        // Re-apply cursor after closeSearch()'s focus() call settles, otherwise
        // re-focusing the editor can reset the cursor to its previous position.
        requestAnimationFrame(() => {
          const s = window.getSelection();
          s?.removeAllRanges();
          s?.addRange(cursorRange);
          editor.focus();
        });
      },
      [buildChip, closeSearch, notifyHasContent, adjustHeight]
    );

    // While the popup is open, mirror cursor moves (arrow keys, mouse clicks)
    // into the search query — typing already triggers onInput on the editor.
    useEffect(() => {
      if (!resourceSearchOpen) return;
      const handler = () => updateQueryFromEditor();
      document.addEventListener('selectionchange', handler);
      return () => document.removeEventListener('selectionchange', handler);
    }, [resourceSearchOpen, updateQueryFromEditor]);

    // ── MutationObserver: sync portals when chips are removed by the browser ──
    // (e.g. backspace key natively deletes a contenteditable="false" span)
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const observer = new MutationObserver(() => {
        if (isRestoringRef.current) return;
        setChipPortals((prev) => prev.filter((p) => editor.contains(p.container)));
        notifyHasContent();
        adjustHeight();
      });

      observer.observe(editor, { childList: true, subtree: true });
      return () => observer.disconnect();
    }, [adjustHeight, notifyHasContent]);

    useEffect(() => {
      if (!autoFocus || disabled) return;
      editorRef.current?.focus();
    }, [autoFocus, disabled]);

    // ── Imperative handle ────────────────────────────────────────────────────
    useImperativeHandle(
      ref,
      () => ({
        focus() {
          editorRef.current?.focus();
        },

        focusAtEnd() {
          const editor = editorRef.current;
          if (!editor) return;
          const place = () => {
            const current = editorRef.current;
            if (!current) return;
            const range = document.createRange();
            range.selectNodeContents(current);
            range.collapse(false);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          };
          editor.focus();
          place();
          requestAnimationFrame(place);
        },

        clear() {
          if (!editorRef.current) return;
          isRestoringRef.current = true;
          editorRef.current.innerHTML = '';
          isRestoringRef.current = false;
          setChipPortals([]);
          setIsEmpty(true);
          onHasContentChange?.(false);
          adjustHeight();
        },

        setPlainText(text: string) {
          if (!editorRef.current) return;
          isRestoringRef.current = true;
          editorRef.current.textContent = text;
          isRestoringRef.current = false;
          setChipPortals([]);
          const has = !!text.trim();
          setIsEmpty(!has);
          onHasContentChange?.(has);
          adjustHeight();
        },

        appendText(text: string) {
          const editor = editorRef.current;
          if (!editor) return;
          editor.appendChild(document.createTextNode(' ' + text));
          setIsEmpty(false);
          onHasContentChange?.(true);
          adjustHeight();
          moveCursorToEnd(editor);
          editor.focus();
        },

        setHTML(html: string) {
          const editor = editorRef.current;
          if (!editor) return;
          isRestoringRef.current = true;
          // eslint-disable-next-line no-unsanitized/property -- restores markup previously read from this editor by getHTML
          editor.innerHTML = html;
          isRestoringRef.current = false;

          // Re-register portals for any chips that were in the saved HTML.
          // Clear each container's innerHTML first so React can render fresh.
          const chipEls = Array.from(editor.querySelectorAll('[data-resource-id]')) as HTMLSpanElement[];
          chipEls.forEach((el) => {
            el.innerHTML = '';
          });
          setChipPortals(
            chipEls.map((el) => ({
              container: el,
              item: {
                external_id: el.dataset.resourceId!,
                resource_type: el.dataset.resourceType as ResourceSearchType,
                title: el.dataset.resourceTitle ?? '',
                session_id: el.dataset.sessionId,
                canvas_id: el.dataset.canvasId
              }
            }))
          );

          const hasContent = !!(editor.textContent?.trim() || chipEls.length > 0);
          setIsEmpty(!hasContent);
          onHasContentChange?.(hasContent);
          adjustHeight();
        },

        getHTML() {
          return editorRef.current?.innerHTML ?? '';
        },

        getContent: extractContent,

        openResourceSearch() {
          const editor = editorRef.current;
          if (editor) {
            // Browsers insert a <br> into empty contenteditable divs to maintain
            // height. If the editor has no real content, remove those <br> nodes
            // before inserting the trigger character — otherwise it lands on
            // a spurious new line.
            const hasRealContent = !!editor.textContent?.trim() || !!editor.querySelector('[data-resource-id]');
            if (!hasRealContent) {
              editor.querySelectorAll('br').forEach((br) => br.remove());
            }
            moveCursorToEnd(editor);
            editor.focus();
          }
          openSearch();
        }
      }),
      [adjustHeight, extractContent, moveCursorToEnd, onHasContentChange, openSearch]
    );

    // ── Render ───────────────────────────────────────────────────────────────
    return (
      <div ref={containerRef} className={`relative flex-1 min-w-0 ${className ?? ''}`}>
        {/* Placeholder (or suggested follow-up) — shown when the editor has no text or chips */}
        {isEmpty && (
          <span
            className="pointer-events-none select-none absolute top-0 left-0 right-0 text-sm md:text-base leading-6 text-content-subtlest"
            aria-hidden="true"
          >
            {suggestion ? (
              <>
                {suggestion}
                <kbd className="ml-2 px-1.5 py-0.5 rounded border border-border-default text-xs font-sans align-middle">
                  Tab
                </kbd>
              </>
            ) : (
              placeholder
            )}
          </span>
        )}

        {/* Contenteditable editor */}
        <div
          ref={editorRef}
          role="textbox"
          aria-label={placeholder}
          aria-placeholder={suggestion ? `${suggestion} (press Tab to use)` : placeholder}
          aria-multiline="true"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          contentEditable={!disabled}
          suppressContentEditableWarning
          className={twMerge(
            'w-full min-h-12 max-h-32 overflow-y-auto text-sm md:text-base outline-none bg-white leading-6',
            editorClassName
          )}
          onInput={() => {
            notifyHasContent();
            adjustHeight();
            if (resourceSearchOpen) updateQueryFromEditor();
          }}
          onPaste={(e) => {
            const items = e.clipboardData?.items;
            const imageFiles: File[] = [];
            const supportedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            let hasUnsupportedImage = false;
            let hasImage = false;

            if (items) {
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (!item.type.startsWith('image/')) continue;
                hasImage = true;
                if (supportedImageTypes.includes(item.type)) {
                  const file = item.getAsFile();
                  if (file) {
                    const ts = Date.now();
                    const ext = item.type.split('/')[1] ?? 'png';
                    const blob = file.slice(0, file.size, file.type);
                    imageFiles.push(
                      new (file.constructor as any)([blob], `pasted-image-${ts}.${ext}`, { type: file.type }) as File
                    );
                  }
                } else {
                  hasUnsupportedImage = true;
                }
              }
            }

            if (hasUnsupportedImage) toast.warning('Only PNG, JPG, JPEG, and WEBP images are supported');

            if (imageFiles.length > 0) {
              e.preventDefault();
              onPastedImages?.(imageFiles);
              return;
            }
            if (hasImage) {
              e.preventDefault();
              return;
            }

            // Plain-text paste — check for a resource URL first, then strip HTML
            e.preventDefault();
            const text = e.clipboardData?.getData('text/plain') ?? '';
            if (text) {
              const parsedUrl = parseResourceUrl(text.trim());
              if (parsedUrl) {
                // Save range before the async gap so we can restore it later
                const savedRange = window.getSelection()?.getRangeAt(0)?.cloneRange() ?? null;
                handleResourceUrlPaste(parsedUrl, text.trim(), savedRange);
                return;
              }

              const sel = window.getSelection();
              if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                range.deleteContents();
                range.insertNode(document.createTextNode(text));
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
              }
              notifyHasContent();
              adjustHeight();
            }
          }}
          onKeyDown={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            // While the resource-search popup is open the editor doubles as
            // the search box: navigation keys drive the popup, all other keys
            // type into the editor and update the query via onInput.
            if (resourceSearchOpen) {
              if (e.key === 'Escape') {
                e.preventDefault();
                closeSearch();
                return;
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => Math.min(i + 1, resourceSearchResults.length - 1));
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => Math.max(i - 1, 0));
                return;
              }
              if (e.key === 'Enter') {
                if (e.shiftKey) {
                  // Shift+Enter ends the search session and lets the editor
                  // insert a newline as usual.
                  closeSearch();
                  return;
                }
                e.preventDefault();
                const item = resourceSearchResults[selectedIndex];
                const alreadyTagged = !!item && chipPortals.some((p) => p.item.external_id === item.external_id);
                if (item && !alreadyTagged) insertChip(item);
                return;
              }
              return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onEnter?.();
              return;
            }
            if (e.key === 'Tab' && !e.shiftKey && isEmpty && suggestion && editorRef.current) {
              e.preventDefault();
              editorRef.current.textContent = suggestion;
              moveCursorToEnd(editorRef.current);
              notifyHasContent();
              adjustHeight();
              onSuggestionAccepted?.(suggestion);
              return;
            }
            // Trigger key: open resource search at current cursor — but only
            // if we're at a word boundary, so typing '@' inside a word (e.g.
            // an email like john@example.com) just inserts the character.
            if (e.key === TRIGGER_CHAR && isAtWordBoundary()) {
              e.preventDefault();
              openSearch();
            }
          }}
        />

        {/* Portals: render a LinkChip into each chip container inside contenteditable */}
        {chipPortals.map(({ container, item }) => {
          const rawPath = searchItemPath(item);
          const href = tenantCode ? toTenantPath(rawPath, tenantCode) : rawPath;
          return createPortal(
            <LinkChip
              icon={RESOURCE_ICON[item.resource_type] ?? FileTextIcon}
              label={item.title}
              href={href}
              newTab
              maxLabelLength={40}
              onDelete={() => removeChip(item.external_id)}
            />,
            container
          );
        })}

        {/* Resource search popup */}
        {resourceSearchOpen && (
          <Popover
            open={resourceSearchOpen}
            anchorEl={containerRef.current}
            onClose={closeSearch}
            placement="top-start"
            className="!max-w-[min(100vw-1rem,480px)] w-full"
          >
            <div className="flex flex-col w-full min-w-[280px] max-w-[480px] p-2">
              {/* Type filter pills */}
              <div className="flex flex-wrap gap-1 pb-2 border-b border-border-default mb-1">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setResourceTypeFilter(null);
                    setSelectedIndex(0);
                  }}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${resourceTypeFilter === null ? 'bg-secondary-blue text-primary-lightblue' : 'bg-background-neutral text-content-subtle hover:bg-bluegray-200'}`}
                >
                  All
                </button>
                {FILTER_OPTIONS.map(({ type, label }) => (
                  <button
                    key={type}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setResourceTypeFilter(resourceTypeFilter === type ? null : type);
                      setSelectedIndex(0);
                    }}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${resourceTypeFilter === type ? 'bg-secondary-blue text-primary-lightblue' : 'bg-background-neutral text-content-subtle hover:bg-bluegray-200'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="max-h-60 min-h-14 overflow-y-auto custom-scrollbar">
                {resourceSearchQuery.trim().length === 0 ? (
                  <div className="px-3 py-5 text-sm text-content-subtlest text-center">
                    {resourceTypeFilter
                      ? `Type to search ${RESOURCE_SEARCH_LABEL[resourceTypeFilter].toLowerCase()}s`
                      : `Keep typing after ${TRIGGER_CHAR} to search for resources to include as context`}
                  </div>
                ) : isResourceSearching ? (
                  <div className="px-3 py-5 text-sm text-content-subtlest text-center">Searching...</div>
                ) : resourceSearchResults.length === 0 ? (
                  <div className="px-3 py-5 text-sm text-content-subtlest text-center">No resources found</div>
                ) : (
                  <ul className="py-1" ref={resultsListRef}>
                    {resourceSearchResults.map((item, idx) => {
                      const alreadyTagged = chipPortals.some((p) => p.item.external_id === item.external_id);
                      const isHighlighted = idx === selectedIndex && !alreadyTagged;
                      return (
                        <li key={`${item.resource_type}:${item.external_id}`}>
                          <button
                            type="button"
                            className={`flex w-full items-center gap-3 px-3 py-2 text-left ${alreadyTagged ? 'opacity-50 cursor-default' : 'hover:bg-bluegray-200'} ${isHighlighted ? 'bg-bluegray-200' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => !alreadyTagged && setSelectedIndex(idx)}
                            onClick={() => !alreadyTagged && insertChip(item)}
                          >
                            <ResourceSearchIcon resourceType={item.resource_type} size={16} />
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate text-sm text-content-default">{item.title}</span>
                            </span>
                            <span className="ml-2 shrink-0 text-xs text-content-subtlest">
                              {(item.resource_type as string) === 'canvas'
                                ? 'Editor Document'
                                : RESOURCE_SEARCH_LABEL[item.resource_type]}
                            </span>
                            {alreadyTagged && (
                              <span className="ml-1 shrink-0 text-xs text-content-subtlest">Added</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </Popover>
        )}
      </div>
    );
  }
);

ResourceTagInput.displayName = 'ResourceTagInput';
export default ResourceTagInput;
